/** Normalized events shared by the Worker and the browser. */

export interface ChatUser {
  uniqueId: string;
  nickname: string;
  avatar?: string;
}

export type StreamEvent =
  | { t: 'chat'; id: string; user: ChatUser; text: string; ts: number }
  | {
      t: 'gift';
      id: string;
      user: ChatUser;
      giftName: string;
      giftId: number;
      count: number;
      diamonds: number;
      streakEnd: boolean;
      image?: string;
      ts: number;
    }
  | { t: 'like'; id: string; user: ChatUser; count: number; total: number; ts: number }
  | { t: 'follow' | 'share' | 'join'; id: string; user: ChatUser; ts: number }
  | { t: 'stats'; viewers?: number; likes?: number; ts: number }
  | { t: 'room'; host: ChatUser; title?: string; isLive: boolean; ts: number }
  | { t: 'control'; action: 'ended' | 'paused' | 'unpaused' | 'suspended'; ts: number };

export type StreamState =
  | 'idle'
  | 'connecting'
  | 'live'
  | 'ended'
  | 'offline'
  /** TikTok says the account is live, but the LIVE could not be opened (usually 18+ restricted). */
  | 'restricted'
  | 'kicked'
  | 'slots_full'
  | 'busy'
  | 'error';

export interface SlotInfo {
  used: number;
  max: number;
}

/** Messages the SlotManager sends to a browser over the slot socket. */
export type SlotServerMessage =
  | {
      t: 'granted';
      jwt: string;
      expiresAt: number;
      slots: SlotInfo;
      whitelisted: boolean;
      tts?: TtsGrant;
      relay?: RelayGrant;
      /** True when this token was minted without the TikTok login but one can be requested (renew with login). */
      loginOnRequest?: boolean;
      /** Feature flags the page acts on. */
      features?: PageFeatures;
      /** Whitelisted pages with diagnostics on: token for POST /api/diag (valid for a few hours). */
      diagToken?: string;
      /**
       * sharedRoom: connect to /room with this token instead of to Euler. `jwt` is then empty;
       * ask for one with `renew` only to fall back to a direct connection.
       */
      room?: RoomGrant;
    }
  | { t: 'jwt'; jwt: string; expiresAt: number; /** Whether this token carries the TikTok login. */ login?: boolean }
  | { t: 'slots_full'; slots: SlotInfo }
  | { t: 'ip_limit'; max: number }
  | { t: 'kicked'; reason: 'priority' }
  | { t: 'busy'; retryAfter: number }
  | { t: 'invalid'; message: string }
  | { t: 'error'; message: string }
  | { t: 'hb' };

/** Runtime flags the browser acts on (mirrors src/worker/flags.ts). */
export interface PageFeatures {
  silenceWatchdog: boolean;
  fastLiveCheck: boolean;
  diagnostics: boolean;
  overlayRejoin: boolean;
}

/** One diagnostics step reported by a whitelisted page (POST /api/diag). */
export interface DiagEvent {
  u: string;
  t: string;
  /** live | reader | overlay | killfeed */
  page: string;
  step: string;
  /** Milliseconds since this page started (or restarted) listening. */
  ms: number;
  detail?: string;
  build?: string;
}

/** Premium (ElevenLabs) voice access, handed only to whitelisted streamers. */
export interface TtsGrant {
  /** Bearer token for POST /api/tts; valid while this slot socket stays open. */
  token: string;
  /** ElevenLabs credits left in this month's budget. */
  remaining: number;
}

/**
 * Logged-in relay for LIVEs Euler cannot open (18+ restricted), handed only to whitelisted
 * streamers when BacaChat has a TikTok login configured.
 */
export interface RelayGrant {
  /** Bearer token for GET /relay; valid while this slot socket stays open. */
  token: string;
}

/** Shared upstream for all pages of one whitelisted streamer (flag sharedRoom). */
export interface RoomGrant {
  /** Bearer token for GET /room; valid while this slot socket stays open. */
  token: string;
}

/**
 * How a room should open its upstream: `euler` (the login only if the flags put it on every
 * whitelisted token), `login` (Euler with the TikTok login), or `relay` (the logged-in container).
 */
export type RoomMode = 'euler' | 'login' | 'relay';
export const ROOM_MODES: readonly RoomMode[] = ['euler', 'login', 'relay'];

/** Messages a browser sends to the SlotManager. */
export type SlotClientMessage = { t: 'renew'; /** Ask for a token with the TikTok login (whitelisted, eulerLoginOnDemand). */ login?: boolean } | { t: 'hb-ack' };

export const SLOT_CLOSE = {
  KICKED: 4001,
  SLOTS_FULL: 4002,
  BUSY: 4003,
  INVALID: 4004,
  IP_LIMIT: 4005,
  ERROR: 4011,
} as const;

/** Euler Stream WebSocket close codes (from @eulerstream/euler-websocket-sdk). */
export const EULER_CLOSE = {
  NORMAL: 1000,
  INTERNAL_SERVER_ERROR: 1011,
  STREAM_END: 4005,
  INVALID_OPTIONS: 4400,
  INVALID_AUTH: 4401,
  NO_PERMISSION: 4403,
  NOT_LIVE: 4404,
  TOO_MANY_CONNECTIONS: 4429,
  TIKTOK_CLOSED_CONNECTION: 4500,
  MAX_LIFETIME_EXCEEDED: 4555,
} as const;

const USERNAME_RE = /^[a-z0-9._]{2,30}$/;

/**
 * Turns whatever someone pasted into a TikTok username: `@name`, `name`, or a profile / LIVE URL
 * such as `https://www.tiktok.com/@name/live?lang=en`. Returns null when nothing valid is found.
 */
export function normalizeUsername(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let u = raw.trim();
  const fromUrl = /tiktok\.com\/@([^/?#\s]+)/i.exec(u) ?? /^@?([^/?#\s]+)\/live\b/i.exec(u);
  if (fromUrl) u = fromUrl[1];
  else if (/tiktok\.com/i.test(u)) return null;
  u = u.replace(/^@+/, '').toLowerCase();
  return USERNAME_RE.test(u) ? u : null;
}
